import React, { useState } from "react";
import classNames from "classnames";
import {
  Icon,
  MonochromeProps,
  StandardProps,
} from "@campgladiator/cgui-core.atoms.icon";
import typographyStyles from "@campgladiator/cgui-core.atoms.typography/typography.module.scss";
import styles from "./input.module.scss";
import { Avatar, AvatarProps } from "@campgladiator/cgui-core.atoms.avatar";

export type Icon =
  | (MonochromeProps & { type: "monochrome" })
  | (StandardProps & { type: "solid" })
  | (StandardProps & { type: "line" });

type InputFieldProps = {
  className?: string;
  disabled?: boolean;
  id: string;
  initialValue?: string;
  inputSize?: "default" | "small";
  invalid?: boolean;
  name: string;
  onInputChange?: (value: string) => void;
} & React.InputHTMLAttributes<HTMLInputElement>;

export type InputProps = {
  icon?: Icon;
  onIconClick?: React.MouseEventHandler<HTMLSpanElement>;
  avatar?: AvatarProps;
} & InputFieldProps;

const setInputTextSizeOnInputFieldSize = (
  inputSize: InputFieldProps["inputSize"]
): "small" | "xsmall" => (inputSize === "small" ? "xsmall" : "small");

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      initialValue,
      className,
      inputSize = "default",
      invalid = false,
      disabled = false,
      onInputChange,
      ...props
    }: InputFieldProps,
    ref
  ) => {
    const [value, setValue] = useState(initialValue);

    return (
      <input
        className={classNames(
          styles.input,
          typographyStyles.paragraph,
          className
        )}
        data-invalid={invalid}
        data-input-size={inputSize}
        data-size={setInputTextSizeOnInputFieldSize(inputSize)}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          onInputChange && onInputChange(e.target.value);
        }}
        disabled={disabled}
        ref={ref}
        {...props}
      />
    );
  }
);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputSize = "default",
      icon,
      avatar,
      onIconClick = () => {},
      ...props
    }: InputProps,
    ref
  ) => {
    const commonIconProps = () => {
      if (!!icon) {
        if (!!icon) {
          return {
            className: styles.icon,
            name: icon.name,
            ["data-input-size"]: inputSize,
            onClick: onIconClick,
          };
        }
      }
    };

    return (
      <div className={styles.inputWrapper}>
        {avatar && (
          <Avatar
            {...avatar}
            size={inputSize === "default" ? "30px" : "20px"}
            className={styles.avatar}
          />
        )}
        <InputField
          className={classNames(
            { [styles.iconIncluded]: icon, [styles.avatarIncluded]: avatar },
            className
          )}
          ref={ref}
          inputSize={inputSize}
          {...props}
        />
        {icon?.type === "solid" && <Icon.Solid {...commonIconProps()} />}
        {icon?.type === "line" && <Icon.Line {...commonIconProps()} />}
        {icon?.type === "monochrome" && (
          <Icon.Monochrome {...commonIconProps()} emphasis={icon.emphasis} />
        )}
      </div>
    );
  }
);

export default Input;
